import Vue from 'vue'
import Vuex from 'vuex'
import cookie from 'vue-cookies'

import router from './../router/index'
import { analytics as $analytics } from '@/utils/analytics'
import { getRobuxWithRate } from '@/utils/rates'
import { PURCHASE_METHODS } from '@/utils/purchase'
import notification from './modules/notification'
import paymentsOrder from './modules/paymentsOrder'
import { rawFetch } from '@/utils/rawFetch'

Vue.use(Vuex)

const currencyMap = {
  rub: '₽',
  usd: '$',
  eur: '€'
}

const initialPromoInfo = {
  _id: '',
  bonus: 0,
  code: '',
  expires: null,
  left: 0,
  max_amount: 0,
  min_amount: 0,
  option: '',
  starts: null,
  type: ''
}

export default new Vuex.Store({
  state: {
    showNotificationPremium: false,
    language: 'ru',
    currency: 'rub',
    ipCountry: 'ru',
    isAuth: false,
    showPreloader: true,
    syncCompleted: true,
    isOpenBurgerMenu: false,
    sign_in: false,
    sign_up: false,
    forget_password: false,
    change_password: false,
    isOpenUserDropMenu: false,
    // Бэкенд ответил 503 — показываем экран обслуживания поверх текущей
    // страницы. Именно флагом, а не переходом на роут /503: переход менял адрес
    // документа, который уже загрузил робот, и на индексируемой странице
    // оказывался noindex вместе с пустым canonical (RBXGD-1119).
    isServiceUnavailable: false,
    isMobile: false,
    methods: {
      isPremiumSalesAvailable: false,
      isLogPassSalesAvailable: false,
      isFastPassSalesAvailable: false
    },
    account: {
      email: '',
      balance: {
        rub: 0,
        usd: 0,
        eur: 0
      },
      userId: '',
      username: '',
      accessToken: '',
      refreshToken: '',
      isVk: 0,
      avatarId: 1,
      avatarUrl: '',
      isPremium: false,
      country: 'ru'
    },
    payments: {
      order: null,
      methodType: 1
    },
    price: {},
    promoCode: {
      bonus: null,
      info: { ...initialPromoInfo }
    },
    isScipGamepass: false,
    isPresaleMode: false,
    rbxAmountStock: 0,
    rates: {
      rub: 0,
      usd: 0,
      eur: 0
    },
    methodRates: {
      giftcards_rate: {
        eur: 0,
        rub: 0,
        usd: 0
      },
      superpass_rate: {
        eur: 0,
        rub: 0,
        usd: 0
      },
      transfer_rate: {
        eur: 0,
        rub: 0,
        usd: 0
      },
      roblox_keys_rate: {
        eur: 0,
        rub: 0,
        usd: 0
      },
      // Поставщик паков работает только в рублях — курс приходит одним числом.
      logpass_rate: 0
    },
    // Включён ли логпасс у поставщика (products-config). Пока не знаем — метод скрыт.
    isPacksAvailable: false,
    purchaseMethod: PURCHASE_METHODS.TRANSFER
  },
  getters: {
    country: state => state.account.country?.toUpperCase(),
    currency: state => state.currency?.toUpperCase(),
    account: state => state.account,
    language: state => state.language,
    balance(state) {
      return Number(state.account.balance[state.currency]) || 0
    },
    currencySign(state) {
      return currencyMap[state.currency]
    },
    getCurrencySign: state => currency => {
      return currencyMap[currency.toLowerCase()]
    },
    price(state) {
      const price = state.price

      return price.prices?.[price.levels?.[state.currency]?.[price.selectedLevel]] || 0
    },
    payments: state => state.payments,
    isPresaleMode: state => state.isPresaleMode,
    isAuth: state => state.isAuth,
    rates(state) {
      const rates = {
        transfer: state.methodRates.transfer_rate[state.currency],
        gift: state.methodRates.giftcards_rate[state.currency],
        superPass: state.methodRates.superpass_rate[state.currency]
      }
      return {
        transfer: getRobuxWithRate(1, rates.transfer),
        gift: getRobuxWithRate(1, rates.gift),
        superPass: getRobuxWithRate(1, rates.superPass),
        // Паки — всегда в рублях, валюта сайта на курс не влияет.
        packs: state.methodRates.logpass_rate ? getRobuxWithRate(1, state.methodRates.logpass_rate) : 0
      }
    },
    isPacksAvailable: state => state.isPacksAvailable
  },
  mutations: {
    setServiceUnavailable(state, value) {
      state.isServiceUnavailable = value
    },
    setLanguage(state, lang) {
      state.language = lang
      localStorage.setItem('language', lang)
    },
    setCurrency(state, currency) {
      state.currency = currency
      localStorage.setItem('currency', currency)
    },
    stateSignIn(state) {
      state.sign_in = !state.sign_in
    },
    stateSignUp(state) {
      state.sign_up = !state.sign_up
    },
    stateForgetPassword(state) {
      state.forget_password = !state.forget_password
    },
    stateChangePassword(state) {
      state.change_password = !state.change_password
    },
    setPaymentData(state, order, type) {
      state.payments.order = order
      state.payments.methodType = type
      state.payments.afterOrder = !order
    },
    clearPaymentData(state) {
      state.payments = {}
    },
    setAccountData(state, data) {
      state.isAuth = true
      state.account.email = data.email
      state.account.balance = data.balance
      state.account.userId = data.user_id
      state.account.username = data.username
      state.account.isVk = data.is_vk
      state.account.country = data.country

      if (state.account.isVk) {
        state.account.avatarUrl = data.avatar
      } else {
        state.account.avatarId = data.avatar
      }

      localStorage.setItem('user_id', data.user_id)
      state.syncCompleted = true
    },
    setTokens(state, tokens) {
      state.account.accessToken = tokens.access
      state.account.refreshToken = tokens.refresh

      cookie.set('access_token', tokens.access, '1d')
      cookie.set('refresh_token', tokens.refresh, '2d')
    },
    setScipGamepass(state, data) {
      state.isScipGamepass = data
    },
    removeAccountData(state) {
      state.account = {}
      state.isAuth = false
      state.showUserDropMenu = false

      localStorage.removeItem('user_id')
    },
    removeTokens(state) {
      cookie.remove('access_token')
      cookie.remove('refresh_token')
    },
    toggleNotification(state) {
      state.showNotificationPremium = false
      setTimeout(() => {
        state.showNotificationPremium = true
      }, 50)
      setTimeout(() => {
        state.showNotificationPremium = false
      }, 5000)
    },
    setPromoBonus(state, bonus) {
      state.promoCode.bonus = bonus
    },
    setPromoInfo(state, info) {
      if (info) {
        state.promoCode.info = { ...info }
        return
      }

      state.promoCode.info = { ...initialPromoInfo }
    },
    setPresaleMode(state, data) {
      state.isPresaleMode = data
    },
    setRbxStock(state, data) {
      state.rbxAmountStock = data
    },
    setRates(state, data) {
      state.rates = data
    },
    setMethodRates(state, data) {
      state.methodRates = data
    },
    setPacksAvailable(state, data) {
      state.isPacksAvailable = data
    },
    setPurchaseMethod(state, data) {
      state.purchaseMethod = data
    }
  },
  actions: {
    showNotificationPremium(ctx) {
      ctx.commit('toggleNotification')
    },
    signIn({ commit }, data) {
      commit('setAccountData', data.data)
      commit('setTokens', data.auth_token)
    },
    authorization({ commit }, token) {
      const requestHeaders = new Headers({
        Authorization: `Bearer ${token}`
      })

      const requestParams = {
        method: 'GET',
        headers: requestHeaders
      }

      fetch('https://users.backend-pier.com/api/v1/user/profile', requestParams)
        .then(res => res.json())
        .then(data => {
          if (data.status) {
            commit('setAccountData', data.data)
            $analytics.setUser(data.data.user_id)
          }
        })
        .catch(err => console.log(err))
    },
    logOut({ commit }, homeRedirect = true) {
      const requestHeaders = new Headers({
        Authorization: `Bearer ${cookie.get('access_token')}`
      })

      const requestParams = {
        method: 'POST',
        headers: requestHeaders
      }

      fetch('https://users.backend-pier.com/api/v1/user/logout', requestParams)
        .then(res => res.json())
        .then(data => {
          if (data.status) {
            commit('removeAccountData')
            commit('removeTokens')

            if (homeRedirect) router.push('/')
          }
        })
        .catch(err => console.log(err))
    },
    buyOrder({ commit }, { order, methodType }) {
      commit('setPaymentData', order, methodType)
      router.push('/payment')
    },
    async getRbxStock({ commit }) {
      try {
        const response = await rawFetch('https://transfer.backend-pier.com/in_stock')
        const data = await response.json()

        if (data.status) {
          commit('setRbxStock', data.robux_amount)
          commit('setPresaleMode', data.presale)
        }
      } catch (err) {}
    },
    async getRates({ commit }) {
      try {
        const userId = localStorage.getItem('user_id') || 'default'

        const response = await rawFetch(`https://prices.backend-pier.com/get_rates/${userId}`)
        const data = await response.json()

        commit('setRates', data.before)
      } catch (err) {}
    },
    async getMethodRates({ commit }) {
      try {
        const response = await rawFetch('https://transfer.backend-pier.com/rates')
        const data = await response.json()

        commit('setMethodRates', data)
      } catch (err) {}
    },
    // Включён ли логпасс у поставщика. Ручка недоступна — метод скрываем:
    // лучше не показать раздел, чем пустить пользователя в неработающий флоу.
    async getPacksConfig({ commit }) {
      try {
        const response = await rawFetch('https://transfer.backend-pier.com/products-config')
        const data = await response.json()

        commit('setPacksAvailable', !!(data.status && data.logpass))
      } catch (err) {}
    }
  },
  modules: {
    notification,
    paymentsOrder
  }
})
